import React, { useCallback, useEffect, useState } from 'react';
import { ArrowPathIcon, EnvelopeIcon, TrashIcon } from '@heroicons/react/24/outline';
import { signupHealthAPI } from '../../services/api';
import { formatDateTime, plural } from '../../utils/format';

const RANGES = [7, 14, 30, 90];

const followUpLink = (email, why) => {
  const subject = encodeURIComponent('Your Serviti AI agent');
  const body = encodeURIComponent(
    `Hi,\n\nI saw you ${why} on Serviti AI but didn't get in. Sorry about that. `
    + 'I can set your AI agent up with you, or send you a fresh link. What suits you?\n\n'
  );
  return `mailto:${email}?subject=${subject}&body=${body}`;
};

const Tile = ({ label, value, hint, tone = 'text-gray-900 dark:text-white' }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
    <p className="text-sm text-gray-600 dark:text-gray-300">{label}</p>
    <p className={`mt-1 text-2xl font-bold ${tone}`}>{value}</p>
    {hint && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{hint}</p>}
  </div>
);

const Section = ({ title, description, action, children }) => (
  <section className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
      <div>
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">{title}</h2>
        {description && <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-300">{description}</p>}
      </div>
      {action}
    </div>
    <div className="overflow-x-auto">{children}</div>
  </section>
);

const Empty = ({ children }) => <p className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400">{children}</p>;

const th = 'px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400';
const td = 'px-4 py-2 text-sm text-gray-800 dark:text-gray-200';
const checkbox = 'h-4 w-4 rounded border-gray-300 text-blue-600';

// Tick rows, or use Delete on one row. Tables share one selection so junk can
// be cleared from both lists in one go.
const SelectAll = ({ emails, selected, onChange, label }) => {
  const all = emails.length > 0 && emails.every((email) => selected.has(email));
  return (
    <input
      type="checkbox"
      aria-label={label}
      className={checkbox}
      checked={all}
      onChange={() => onChange(emails, !all)}
    />
  );
};

const RowActions = ({ email, why, onDelete }) => (
  <div className="flex items-center gap-3 whitespace-nowrap">
    <a href={followUpLink(email, why)} className="inline-flex items-center gap-1 font-semibold text-blue-700 hover:underline">
      <EnvelopeIcon className="h-4 w-4" aria-hidden="true" /> Follow up
    </a>
    <button
      type="button"
      onClick={() => onDelete([email])}
      aria-label={`Delete ${email}`}
      className="inline-flex items-center gap-1 font-semibold text-red-700 hover:underline"
    >
      <TrashIcon className="h-4 w-4" aria-hidden="true" /> Delete
    </button>
  </div>
);

// FastAPI validation errors arrive as a list; only a sentence is shown.
const detailText = (requestError, fallback) => {
  const detail = requestError?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
};

const SignupHealthPage = () => {
  const [days, setDays] = useState(14);
  const [includeInternal, setIncludeInternal] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [confirming, setConfirming] = useState(null); // emails awaiting confirmation
  const [deleting, setDeleting] = useState(false);
  const [notice, setNotice] = useState('');

  const toggle = (emails, on) => setSelected((current) => {
    const next = new Set(current);
    emails.forEach((email) => (on ? next.add(email) : next.delete(email)));
    return next;
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await signupHealthAPI.get({ days, include_internal: includeInternal });
      setData(response.data);
      // Keep only ticks on rows still shown: a tick on a row that left the
      // lists (another period, test addresses hidden) must not be deleted
      // unseen by "Delete selected".
      const shown = new Set(
        [...(response.data?.stuck || []), ...(response.data?.signin_without_account || [])].map((row) => row.email),
      );
      setSelected((current) => new Set([...current].filter((email) => shown.has(email))));
    } catch (requestError) {
      setError(detailText(requestError, 'Could not load signup health.'));
    } finally {
      setLoading(false);
    }
  }, [days, includeInternal]);

  useEffect(() => { load(); }, [load]);

  const confirmDelete = async () => {
    const emails = confirming || [];
    setDeleting(true);
    setError('');
    try {
      const response = await signupHealthAPI.deleteAttempts(emails);
      const kept = response.data?.accounts_kept || [];
      setNotice(
        `Deleted ${plural(emails.length, 'sign-up attempt')}.`
        + (kept.length ? ` ${plural(kept.length, 'address has', 'addresses have')} an account, so only the attempt history was removed.` : '')
      );
      setSelected((current) => {
        const next = new Set(current);
        emails.forEach((email) => next.delete(email));
        return next;
      });
      setConfirming(null);
      await load();
    } catch (requestError) {
      setError(detailText(requestError, 'Could not delete those sign-up attempts.'));
      setConfirming(null);
    } finally {
      setDeleting(false);
    }
  };

  const funnel = data?.funnel || {};
  const stuck = data?.stuck || [];
  const deadEnds = data?.signin_without_account || [];
  const failures = data?.failures || [];
  const rate = funnel.completion_rate == null ? '—' : `${Math.round(funnel.completion_rate * 100)}%`;

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Signup health</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            Who tried to sign up or sign in and didn&rsquo;t get in, and why. Follow them up from here.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-sm text-gray-700 dark:text-gray-200">
            <span className="sr-only">Period</span>
            <select
              value={days}
              onChange={(event) => setDays(Number(event.target.value))}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
            >
              {RANGES.map((value) => <option key={value} value={value}>Last {value} days</option>)}
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
            <input
              type="checkbox"
              checked={includeInternal}
              onChange={(event) => setIncludeInternal(event.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            Include @serviti.ai test addresses
          </label>
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
            Refresh
          </button>
        </div>
      </div>

      {error && <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}
      {notice && <div role="status" className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">{notice}</div>}

      {selected.size > 0 && (
        <div className="sticky top-2 z-10 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 shadow-sm">
          <span>{plural(selected.size, 'address', 'addresses')} selected</span>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setSelected(new Set())} className="rounded-md px-3 py-1.5 font-medium text-gray-700 hover:bg-white">
              Clear
            </button>
            <button
              type="button"
              onClick={() => setConfirming([...selected])}
              className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 font-semibold text-white hover:bg-red-700"
            >
              <TrashIcon className="h-4 w-4" aria-hidden="true" /> Delete selected ({selected.size})
            </button>
          </div>
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Tile label="Sent a signup code" value={funnel.people_sent_a_code ?? 0} hint={plural(funnel.people_sent_a_code ?? 0, 'person', 'people')} />
            <Tile label="Finished signing up" value={funnel.completed ?? 0} hint={`${rate} of people sent a code`} tone="text-green-700 dark:text-green-400" />
            <Tile label="Didn't finish" value={funnel.not_completed ?? 0} hint="Code sent, no account yet" tone={funnel.not_completed ? 'text-red-600' : undefined} />
            <Tile label="Signed in with no account" value={deadEnds.length} hint="Wanted to sign up, used sign-in" tone={deadEnds.length ? 'text-red-600' : undefined} />
          </div>

          <Section
            title="Didn't finish signing up"
            description="They were emailed a signup code and never created their workspace. Newest first."
          >
            {stuck.length ? (
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead><tr>
                  <th className={th}>
                    <SelectAll emails={stuck.map((row) => row.email)} selected={selected} onChange={toggle} label="Select everyone who didn't finish" />
                  </th>
                  <th className={th}>Email</th><th className={th}>Codes sent</th><th className={th}>Code attempts</th>
                  <th className={th}>Last code</th><th className={th}>Last problem</th><th className={th}><span className="sr-only">Actions</span></th>
                </tr></thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {stuck.map((row) => (
                    <tr key={row.email}>
                      <td className={td}>
                        <input type="checkbox" aria-label={`Select ${row.email}`} className={checkbox}
                          checked={selected.has(row.email)} onChange={(event) => toggle([row.email], event.target.checked)} />
                      </td>
                      <td className={td}>{row.email}{row.internal && <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">team</span>}</td>
                      <td className={td}>{row.codes_sent}</td>
                      <td className={td}>{row.code_attempts}</td>
                      <td className={td}>{formatDateTime(row.last_code_at)}</td>
                      <td className={td}>{row.last_problem || 'No error recorded: likely never opened the email, or it went to spam'}</td>
                      <td className={td}>
                        <RowActions email={row.email} why="started signing up" onDelete={setConfirming} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <Empty>Everyone who was sent a signup code in this period finished.</Empty>}
          </Section>

          <Section
            title="Tried to sign in without an account"
            description="They typed their email into sign-in instead of signup. They are now emailed a link to sign up."
          >
            {deadEnds.length ? (
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead><tr>
                  <th className={th}>
                    <SelectAll emails={deadEnds.map((row) => row.email)} selected={selected} onChange={toggle} label="Select everyone who signed in without an account" />
                  </th>
                  <th className={th}>Email</th><th className={th}>Attempts</th><th className={th}>Last attempt</th>
                  <th className={th}>Signed up since</th><th className={th}><span className="sr-only">Actions</span></th>
                </tr></thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {deadEnds.map((row) => (
                    <tr key={row.email}>
                      <td className={td}>
                        <input type="checkbox" aria-label={`Select ${row.email}`} className={checkbox}
                          checked={selected.has(row.email)} onChange={(event) => toggle([row.email], event.target.checked)} />
                      </td>
                      <td className={td}>{row.email}</td>
                      <td className={td}>{row.attempts}</td>
                      <td className={td}>{formatDateTime(row.last_attempt_at)}</td>
                      <td className={td}>{row.signed_up_since ? 'Yes' : 'No'}</td>
                      <td className={td}>
                        <RowActions email={row.email} why="tried to sign in" onDelete={setConfirming} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <Empty>No one tried to sign in without an account in this period.</Empty>}
          </Section>

          <Section title="Why attempts failed" description="Every recorded failure in this period, most common first.">
            {failures.length ? (
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead><tr><th className={th}>What happened</th><th className={th}>Times</th><th className={th}>People</th></tr></thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {failures.map((row) => (
                    <tr key={`${row.event}-${row.detail || ''}`}>
                      <td className={td}>{row.label}</td>
                      <td className={td}>{row.count}</td>
                      <td className={td}>{row.people}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <Empty>No failures recorded in this period.</Empty>}
          </Section>

          <p className="text-xs text-gray-500 dark:text-gray-400">
            {data.telemetry_since
              ? `Failure reasons are recorded from ${formatDateTime(data.telemetry_since)}. `
              : 'Failure reasons are recorded from the next signup attempt onwards. '}
            &ldquo;Didn&rsquo;t finish&rdquo; is read from the codes themselves, so it covers the whole period.
          </p>
        </>
      )}

      {confirming && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div role="dialog" aria-modal="true" aria-labelledby="delete-signups-title" className="w-full max-w-md rounded-lg bg-white shadow-xl dark:bg-gray-900">
            <div className="border-b border-gray-200 px-5 py-4 dark:border-gray-700">
              <h2 id="delete-signups-title" className="text-lg font-semibold text-gray-900 dark:text-white">
                Delete {plural(confirming.length, 'sign-up attempt')}?
              </h2>
            </div>
            <div className="space-y-3 p-5 text-sm text-gray-700 dark:text-gray-300">
              <p className="break-all font-medium">
                {confirming.slice(0, 5).join(', ')}{confirming.length > 5 ? ` and ${confirming.length - 5} more` : ''}
              </p>
              <p>
                Removes their signup codes and history from this page and its numbers. Accounts and sign-in
                codes are never touched. Anyone real can still sign up again.
              </p>
            </div>
            <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-4 dark:border-gray-700">
              <button type="button" onClick={() => setConfirming(null)} disabled={deleting}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300">
                Cancel
              </button>
              <button type="button" onClick={confirmDelete} disabled={deleting}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60">
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignupHealthPage;
